import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0f0f13;
  color: #fff;
  font-family: "Pretendard", sans-serif;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  max-width: 500px;
  width: 90%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 50px rgba(0,0,0,0.3);
`;

const IconWrapper = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  color: ${props => props.success ? '#4cd137' : '#e84118'};
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 10px;
`;

const Message = styled.p`
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 30px;
  line-height: 1.5;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #4834d4 0%, #686de0 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, fail
  const [message, setMessage] = useState('결제 승인 처리 중입니다...');
  const isProcessing = useRef(false);

  useEffect(() => {
    const confirmPayment = async () => {
      // Prevent double invocation
      if (isProcessing.current) return;
      isProcessing.current = true;

      const paymentKey = searchParams.get('paymentKey');
      const orderId = searchParams.get('orderId');
      const amount = searchParams.get('amount');
      
      // orderName에서 개월 수 추출 (예: "FUTURE AI CLASS - 3개월권")
      // 하지만 URL 파라미터에는 orderName이 없을 수 있습니다.
      // 토스 리다이렉트 URL에는 orderName이 포함되지 않을 수 있음.
      // 따라서 localStorage 등을 통해 개월 수를 전달하거나, amount로 추정해야 함.
      // 여기서는 localStorage를 사용하는 것이 안전함.
      const storedMonths = localStorage.getItem('selectedPlanMonths');
      const months = storedMonths ? parseInt(storedMonths) : 1; // Default to 1 if missing
      
      const user = localStorage.getItem('user');

      if (!paymentKey || !orderId || !amount) {
        setStatus('fail');
        setMessage('결제 정보가 올바르지 않습니다.');
        return;
      }

      if (!user) {
        setStatus('fail');
        setMessage('로그인 정보가 만료되었습니다. 다시 로그인해주세요.');
        return;
      }

      try {
        const response = await fetch('/api/confirm-payment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentKey,
            orderId,
            amount: parseInt(amount),
            username: user,
            months: months
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage('결제가 성공적으로 완료되었습니다.\n서비스 이용 기간이 연장되었습니다.');
          localStorage.removeItem('selectedPlanMonths'); // Clean up
        } else {
          setStatus('fail');
          setMessage(data.detail || '결제 승인에 실패했습니다.');
        }
      } catch (error) {
        console.error('Payment Confirm Error:', error);
        setStatus('fail');
        setMessage('서버 통신 중 오류가 발생했습니다.');
      }
    };

    confirmPayment();
  }, [searchParams]);

  return (
    <PageWrapper>
      <Card>
        <IconWrapper success={status === 'success'}>
          {status === 'success' ? <FiCheckCircle /> : (status === 'fail' ? <FiXCircle /> : <div className="spinner-border text-primary" role="status" />)}
        </IconWrapper>
        <Title>{status === 'success' ? '결제 완료!' : (status === 'fail' ? '결제 실패' : '처리 중...')}</Title>
        <Message>{message}</Message>
        <Button onClick={() => navigate('/')}>홈으로 돌아가기</Button>
      </Card>
    </PageWrapper>
  );
};

export default PaymentSuccessPage;
